/* ============================================================================
 * Train Tuner — a small dev overlay for positioning the train cars live.
 *
 * Open/close:  press the backtick key `  (or click the 🚂 button, bottom-left)
 *
 * What it does:
 *   - Sliders for each car type's vertical seat (groundOffset), plus the whole
 *     train's car spacing, engine gap, sprite scale and speed.
 *   - Changes apply instantly to the train on screen AND to every train that
 *     spawns afterward (via window.__trainTune, read by the Train constructor).
 *   - "Freeze" stops the train so you can nudge it while it sits still.
 *   - "Spawn train" launches one immediately.
 *   - "Copy code" copies map.js-ready lines so you can paste the final numbers
 *     into the Train constructor and make them permanent.
 *
 * This file is a plain (non-module) script and only touches window.engine, so
 * it has zero effect on gameplay unless you open the panel.
 * ========================================================================== */
(function () {
  'use strict';

  const CAR_TYPES = ['engine', 'coalcar', 'boxcar', 'tanker', 'passenger', 'caboose'];

  // Seed defaults — mirror the values in map.js Train constructor.
  const DEFAULTS = {
    speed: 60,
    carSpacing: 198,
    engineGap: 20,
    scale: 0.45,
    groundOffsets: { engine: 44, coalcar: 44, boxcar: 44, tanker: 44, passenger: 44, caboose: 44 }
  };

  // The single source of truth the Train constructor reads.
  const tune = window.__trainTune = window.__trainTune || {
    speed: DEFAULTS.speed,
    carSpacing: DEFAULTS.carSpacing,
    engineGap: DEFAULTS.engineGap,
    scale: DEFAULTS.scale,
    groundOffsets: Object.assign({}, DEFAULTS.groundOffsets)
  };

  let frozen = false;
  let panel = null;
  const linked = {};                 // car type -> selected for group move
  for (const t of CAR_TYPES) linked[t] = false;

  // ---- helpers ------------------------------------------------------------
  function liveTrains() {
    const e = window.engine;
    if (!e || !Array.isArray(e.entities)) return [];
    return e.entities.filter(x => (x.kind || (x.constructor && x.constructor.name)) === 'Train');
  }
  function liveSpawner() {
    const e = window.engine;
    if (!e || !Array.isArray(e.entities)) return null;
    return e.entities.find(x => (x.kind || (x.constructor && x.constructor.name)) === 'TrainSpawner') || null;
  }

  // Push current tune values onto every train on screen. Called on a light
  // interval so freeze state and newly-spawned trains stay in sync.
  function apply() {
    for (const t of liveTrains()) {
      t.carSpacing = tune.carSpacing;
      t.engineGap = tune.engineGap;
      t.scale = tune.scale;
      t.groundOffsets = Object.assign({}, t.groundOffsets, tune.groundOffsets);
      t.speed = frozen ? 0 : tune.speed;
    }
  }
  setInterval(apply, 150);

  function codeText() {
    const g = tune.groundOffsets;
    const go = CAR_TYPES.map(t => `${t}: ${Math.round(g[t])}`).join(', ');
    return [
      `this.speed = ${Math.round(tune.speed)};`,
      `this.carSpacing = ${Math.round(tune.carSpacing)};`,
      `this.engineGap = ${Math.round(tune.engineGap)};`,
      `this.scale = ${(+tune.scale).toFixed(3)};`,
      `this.groundOffsets = { ${go} };`
    ].join('\n');
  }

  // ---- UI -----------------------------------------------------------------
  function row(label, min, max, step, get, set) {
    const wrap = document.createElement('label');
    wrap.style.cssText = 'display:grid;grid-template-columns:96px 1fr 52px;align-items:center;gap:8px;margin:5px 0;font:12px system-ui,sans-serif;color:#e8e8f0;';
    const name = document.createElement('span'); name.textContent = label;
    const slider = document.createElement('input');
    slider.type = 'range'; slider.min = min; slider.max = max; slider.step = step; slider.value = get();
    slider.style.width = '100%';
    const num = document.createElement('input');
    num.type = 'number'; num.min = min; num.max = max; num.step = step; num.value = get();
    num.style.cssText = 'width:52px;background:#1c1c2b;color:#fff;border:1px solid #444;border-radius:5px;padding:2px 4px;font:12px system-ui;';
    const sync = (v) => { v = +v; if (isNaN(v)) return; slider.value = v; num.value = v; set(v); apply(); refreshCode(); };
    slider.addEventListener('input', () => sync(slider.value));
    num.addEventListener('input', () => sync(num.value));
    wrap.append(name, slider, num);
    wrap._sync = () => { slider.value = get(); num.value = get(); };
    return wrap;
  }

  const GO_MIN = -40, GO_MAX = 140;

  // A ground-offset row with a link checkbox. When its type is linked, dragging
  // it moves every other linked type by the same delta (relative offsets kept).
  function carRow(t) {
    const wrap = document.createElement('label');
    wrap.style.cssText = 'display:grid;grid-template-columns:18px 82px 1fr 52px;align-items:center;gap:8px;margin:5px 0;font:12px system-ui,sans-serif;color:#e8e8f0;';
    const chk = document.createElement('input');
    chk.type = 'checkbox'; chk.checked = linked[t]; chk.title = 'Link — move with other checked cars';
    chk.style.cssText = 'width:15px;height:15px;accent-color:#1f6feb;cursor:pointer;';
    chk.addEventListener('change', () => { linked[t] = chk.checked; });
    const name = document.createElement('span'); name.textContent = t;
    const slider = document.createElement('input');
    slider.type = 'range'; slider.min = GO_MIN; slider.max = GO_MAX; slider.step = 0.5;
    slider.value = tune.groundOffsets[t]; slider.style.width = '100%';
    const num = document.createElement('input');
    num.type = 'number'; num.min = GO_MIN; num.max = GO_MAX; num.step = 0.5; num.value = tune.groundOffsets[t];
    num.style.cssText = 'width:52px;background:#1c1c2b;color:#fff;border:1px solid #444;border-radius:5px;padding:2px 4px;font:12px system-ui;';

    const clamp = (v) => Math.min(GO_MAX, Math.max(GO_MIN, v));
    const change = (v) => {
      v = clamp(+v); if (isNaN(v)) return;
      const delta = v - tune.groundOffsets[t];
      if (linked[t] && delta !== 0) {
        for (const u of CAR_TYPES) {
          if (u !== t && linked[u]) tune.groundOffsets[u] = clamp(tune.groundOffsets[u] + delta);
        }
      }
      tune.groundOffsets[t] = v;
      apply(); refreshRows();
    };
    slider.addEventListener('input', () => change(slider.value));
    num.addEventListener('input', () => change(num.value));
    wrap.append(chk, name, slider, num);
    wrap._sync = () => { chk.checked = linked[t]; slider.value = tune.groundOffsets[t]; num.value = tune.groundOffsets[t]; };
    return wrap;
  }

  let codeBox = null;
  const rowRefs = [];
  function refreshCode() { if (codeBox) codeBox.value = codeText(); }
  function refreshRows() { for (const r of rowRefs) r._sync && r._sync(); refreshCode(); }

  function build() {
    const mount = document.getElementById('tuner-mount-point');
    if (mount) {
      panel = mount;
      panel.innerHTML = '';
      panel.style.cssText = 'font:13px system-ui,sans-serif;color:#fff;';
    } else {
      panel = document.createElement('div');
      panel.style.cssText = [
        'position:fixed', 'left:14px', 'bottom:64px', 'z-index:99999', 'width:340px',
        'background:rgba(18,18,30,0.96)', 'border:1px solid #3a3a55', 'border-radius:12px',
        'padding:12px 14px', 'box-shadow:0 10px 40px rgba(0,0,0,0.6)', 'backdrop-filter:blur(6px)',
        'font:13px system-ui,sans-serif', 'color:#fff', 'display:none'
      ].join(';');
    }

    if (!mount) {
      const title = document.createElement('div');
      title.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;';
      title.innerHTML = `<b style="font-size:14px;display:inline-flex;align-items:center;gap:4px;">${window.GameIcons ? window.GameIcons.get('tools', 14) : '🚂'} Train Tuner</b>`;
      const close = document.createElement('button');
      close.textContent = '✕';
      close.style.cssText = 'background:none;border:none;color:#aaa;font-size:15px;cursor:pointer;';
      close.onclick = toggle;
      title.appendChild(close);
      panel.appendChild(title);
    }

    const hint = document.createElement('div');
    hint.style.cssText = 'display:flex;justify-content:space-between;align-items:center;color:#9aa;margin:4px 0 2px;font-size:11px;';
    const hintText = document.createElement('span');
    hintText.textContent = 'Vertical seat per car (+ down / – up):';
    const allLink = document.createElement('label');
    allLink.style.cssText = 'display:flex;align-items:center;gap:4px;cursor:pointer;color:#9aa;';
    const allChk = document.createElement('input');
    allChk.type = 'checkbox'; allChk.style.cssText = 'width:13px;height:13px;accent-color:#1f6feb;cursor:pointer;';
    allChk.addEventListener('change', () => {
      for (const t of CAR_TYPES) linked[t] = allChk.checked;
      refreshRows();
    });
    allLink.append(allChk, document.createTextNode('link all'));
    hint.append(hintText, allLink);
    panel.appendChild(hint);

    for (const t of CAR_TYPES) {
      const r = carRow(t);
      rowRefs.push(r); panel.appendChild(r);
    }

    const hr = document.createElement('div');
    hr.style.cssText = 'height:1px;background:#333;margin:8px 0;';
    panel.appendChild(hr);

    const spacing = row('car spacing', 100, 320, 1, () => tune.carSpacing, v => tune.carSpacing = v);
    const gap = row('engine gap', 0, 140, 1, () => tune.engineGap, v => tune.engineGap = v);
    const scale = row('scale', 0.2, 0.8, 0.005, () => tune.scale, v => tune.scale = v);
    const speed = row('speed', 0, 200, 1, () => tune.speed, v => tune.speed = v);
    rowRefs.push(spacing, gap, scale, speed);
    panel.append(spacing, gap, scale, speed);

    // buttons
    const btns = document.createElement('div');
    btns.style.cssText = 'display:flex;gap:6px;margin-top:10px;flex-wrap:wrap;';
    const mkBtn = (label, fn, bg) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText = `flex:1;min-width:70px;background:${bg || '#2f2f4a'};color:#fff;border:none;border-radius:7px;padding:7px 6px;font:12px system-ui;cursor:pointer;`;
      b.onclick = fn; return b;
    };

    const freezeBtn = mkBtn('Freeze', () => {
      frozen = !frozen;
      freezeBtn.innerHTML = frozen 
        ? `${window.GameIcons ? window.GameIcons.get('unlocked', 11, 'margin-right:3px; vertical-align:middle; filter:brightness(10);') : ''} Resume` 
        : `${window.GameIcons ? window.GameIcons.get('idle', 11, 'margin-right:3px; vertical-align:middle; filter:brightness(10);') : ''} Freeze`;
      freezeBtn.style.background = frozen ? '#1f6feb' : '#2f2f4a';
      apply();
    });
    freezeBtn.innerHTML = `${window.GameIcons ? window.GameIcons.get('idle', 11, 'margin-right:3px; vertical-align:middle; filter:brightness(10);') : ''} Freeze`;

    const spawnBtn = mkBtn('Spawn', () => {
      const s = liveSpawner();
      if (s) { s.timer = 0; }
      else { flash('No rail line on this map — load a map with tracks.'); }
    });
    spawnBtn.innerHTML = `${window.GameIcons ? window.GameIcons.get('wave', 11, 'margin-right:3px; vertical-align:middle; filter:brightness(10);') : ''} Spawn`;

    const copyBtn = mkBtn('Copy code', () => {
      const txt = codeText();
      navigator.clipboard && navigator.clipboard.writeText(txt);
      flash('Copied map.js lines to clipboard.');
    });
    copyBtn.innerHTML = `${window.GameIcons ? window.GameIcons.get('permits', 11, 'margin-right:3px; vertical-align:middle; filter:brightness(10);') : ''} Copy code`;

    const resetBtn = mkBtn('↺ Reset', () => {
      tune.speed = DEFAULTS.speed; tune.carSpacing = DEFAULTS.carSpacing;
      tune.engineGap = DEFAULTS.engineGap; tune.scale = DEFAULTS.scale;
      tune.groundOffsets = Object.assign({}, DEFAULTS.groundOffsets);
      apply(); refreshRows();
    });
    btns.append(freezeBtn, spawnBtn, copyBtn, resetBtn);
    panel.appendChild(btns);

    // dedicated full-width Save button (persists to the server for everyone)
    const saveBtn = document.createElement('button');
    saveBtn.innerHTML = `${window.GameIcons ? window.GameIcons.get('checked', 13, 'margin-right:4px; vertical-align:middle; filter:brightness(10);') : '💾'} Save config to server`;
    saveBtn.style.cssText = 'width:100%;margin-top:8px;background:#2ea043;color:#fff;border:none;border-radius:8px;padding:9px;font:13px system-ui;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:4px;';
    saveBtn.onclick = () => saveConfig(saveBtn);
    panel.appendChild(saveBtn);

    codeBox = document.createElement('textarea');
    codeBox.readOnly = true; codeBox.rows = 5;
    codeBox.style.cssText = 'width:100%;margin-top:10px;background:#12121e;color:#7ee787;border:1px solid #333;border-radius:7px;padding:7px;font:11px ui-monospace,Menlo,monospace;resize:vertical;box-sizing:border-box;';
    panel.appendChild(codeBox);

    const foot = document.createElement('div');
    foot.style.cssText = 'color:#778;font-size:10px;margin-top:6px;';
    foot.textContent = mount ? 'Values also apply to future trains this session.' : 'Toggle: backtick ` key. Values also apply to future trains this session.';
    panel.appendChild(foot);

    if (!mount) {
      document.body.appendChild(panel);
    }
    refreshRows();
  }

  let toast = null;
  function flash(msg) {
    if (!toast) {
      toast = document.createElement('div');
      toast.style.cssText = 'position:fixed;left:14px;bottom:16px;z-index:100000;background:#1f6feb;color:#fff;padding:8px 12px;border-radius:8px;font:12px system-ui;box-shadow:0 4px 16px rgba(0,0,0,.5);transition:opacity .3s;';
      document.body.appendChild(toast);
    }
    toast.textContent = msg; toast.style.opacity = '1';
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { toast.style.opacity = '0'; }, 1800);
  }

  // ---- persistence --------------------------------------------------------
  function currentConfig() {
    return {
      speed: +tune.speed, carSpacing: +tune.carSpacing, engineGap: +tune.engineGap,
      scale: +tune.scale, groundOffsets: Object.assign({}, tune.groundOffsets)
    };
  }
  async function saveConfig(btn) {
    const token = localStorage.getItem('parade_token');
    if (!token) { flash('Log in as admin to save.'); return; }
    const old = btn ? btn.textContent : '';
    if (btn) { btn.textContent = 'Saving…'; btn.disabled = true; }
    try {
      const res = await fetch(`${window.API_ORIGIN || ''}/api/admin/train-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ config: currentConfig() })
      });
      if (res.ok) flash('Saved — every train uses this now.');
      else if (res.status === 401 || res.status === 403) flash('Admin access required to save.');
      else flash('Save failed (' + res.status + ').');
    } catch (e) { flash('Save failed — server unreachable.'); }
    finally { if (btn) { btn.textContent = old; btn.disabled = false; } }
  }

  // Load any saved config at startup so trains spawn already tuned.
  (async function loadConfig() {
    try {
      const res = await fetch(`${window.API_ORIGIN || ''}/api/config`);
      const data = await res.json().catch(() => ({}));
      const c = data && data.train_config;
      if (c && typeof c === 'object') {
        if (c.speed != null) tune.speed = c.speed;
        if (c.carSpacing != null) tune.carSpacing = c.carSpacing;
        if (c.engineGap != null) tune.engineGap = c.engineGap;
        if (c.scale != null) tune.scale = c.scale;
        if (c.groundOffsets) tune.groundOffsets = Object.assign({}, DEFAULTS.groundOffsets, c.groundOffsets);
        apply(); refreshRows();
      }
    } catch (e) { /* offline / no server — fall back to defaults */ }
  })();

  function toggle() {
    if (!panel) build();
    const showing = panel.style.display !== 'none';
    panel.style.display = showing ? 'none' : 'block';
    if (!showing) refreshRows();
  }

  // Expose to window so Admin Panel in main.js can render/update
  window.trainTuner = {
    build: build,
    refreshRows: refreshRows,
    toggle: toggle
  };
})();
